import { Component, ChangeDetectionStrategy, Injector, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TuiDialogService } from '@taiga-ui/core';
import { fixRouteParam } from '@src/utils';
import { BaseResizableComponent } from '@src/app/modules/base';
import { OrganisationService } from '@src/core/services';
import { AuthUserService } from '@src/app/modules/auth';
import { PlacemarkType, PlacemarksService } from '@src/app/modules/map';
import { CitySwitcherService } from '@src/app/modules/search-cities/city-switcher';

import {
  LoyaltyProgramApiService,
  LpDiscountDialogService,
  LpViewProductsStoreService,
  ResultDialogService,
} from '../services';
import { ProductDialogComponent } from '../product-dialog';
import { LoyaltyProgramProduct } from '../models';
import { LoyaltyProgramAction } from '../types';
import { getBalloonContentByProduct } from '../utils';

import { BreadcrumbItem } from './components';
import { ACTION_MAP_RESULT } from './constants';

@Component({
  selector: 'lp-view-product',
  templateUrl: './lp-view-product.component.html',
  styleUrls: ['./lp-view-product.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LpViewProductComponent extends BaseResizableComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly resultDialogService = inject(ResultDialogService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly loyaltyProgramApiService = inject(LoyaltyProgramApiService);
  private readonly router = inject(Router);
  private readonly organisationService = inject(OrganisationService);
  private readonly authUserService = inject(AuthUserService);
  private readonly lpDiscountDialogService = inject(LpDiscountDialogService);
  private readonly placemarksService = inject(PlacemarksService);
  private readonly citySwitcherService = inject(CitySwitcherService);
  private readonly lpViewProductsStoreService = inject(LpViewProductsStoreService);

  readonly currentCity$ = this.citySwitcherService.currentCity$;

  unionId?: string;
  productId?: string;

  loading = true;
  product?: LoyaltyProgramProduct;
  breadcrumbs: BreadcrumbItem[] = [];

  /** Признак использования unionId в запросах */
  useUnionId = false;

  /** Признак внешнего объявления. Объявление создано другой ассоциацией */
  isExternalProduct = false;

  mapVisible = false;
  placemarksForMap: PlacemarkType[] = [];

  private discountDialogOpen = false;

  override ngOnInit(): void {
    super.ngOnInit();

    this.route.paramMap.pipe(takeUntil(this.destroyed$$)).subscribe(async params => {
      this.productId = fixRouteParam(params.get('loyaltyId'));

      this.route.queryParamMap.pipe(takeUntil(this.destroyed$$)).subscribe(queryParams => {
        this.unionId = fixRouteParam(queryParams.get('unionId'));
        const collectionId = fixRouteParam(queryParams.get('collectionId'));
        const ref = fixRouteParam(queryParams.get('ref'));
        this.cdr.markForCheck();

        this.refresh(this.productId, this.unionId, collectionId, ref).then(() => {
          // Скролляем в самый вверх, т.к. после перехода в похожие предложения запоминается позиция
          const element = document.getElementById('viewProductScrollbar');
          if (element) {
            element.scrollTop = 0;
          }

          this.cdr.markForCheck();
        });
      });
    });

    this.lpViewProductsStoreService.data$.pipe(takeUntil(this.destroyed$$)).subscribe(() => {
      if (!this.productId) return;

      this.product = this.lpViewProductsStoreService.getData(this.productId);
      this.loading = !this.product;

      if (this.product) {
        this.placemarksForMap = getBalloonContentByProduct(this.product, this.unionId).map(item =>
          this.placemarksService.getPlacemarks(item),
        );

        this.setUseUnionId();
        this.setIsExternalProduct();
      }

      this.updateBreadcrumbs();
      this.cdr.markForCheck();
    });

    this.organisationService.currentParentOrganisation$.pipe(takeUntil(this.destroyed$$)).subscribe(() => {
      this.setUseUnionId();
      this.setIsExternalProduct();
    });
  }

  get imageIds(): string[] {
    return this.product?.images?.map(item => item.id!) ?? [];
  }

  /** Заполняем признак использования unionId */
  setUseUnionId() {
    this.useUnionId = this.product?.unionId
      ? this.organisationService.currentParentOrganisation$.value?.id !== this.product.unionId
      : false;
    this.cdr.markForCheck();
  }

  setIsExternalProduct() {
    this.isExternalProduct = this.product?.unionId
      ? this.organisationService.currentParentOrganisation$.value?.id !== this.product.unionId
      : false;
    this.cdr.markForCheck();
  }

  /** ID ассоциации внешней базы */
  get externalUnionId(): string | undefined {
    return this.useUnionId ? this.product?.unionId : undefined;
  }

  get isUserOrganisation() {
    return this.product?.contacts.company.id === this.authUserService.user?.organisationId;
  }

  get isUserAssociation() {
    return this.product?.contacts.company.parentOrganisationId === this.authUserService.user?.parentOrganisationId;
  }

  onGetDiscount() {
    if (!this.product || this.discountDialogOpen) return;

    this.discountDialogOpen = true;

    if (this.product.terms.length > 1) {
      this.lpDiscountDialogService.openDiscountGroupsDialog(this.product, this.externalUnionId).subscribe({
        error: () => {
          this.discountDialogOpen = false;
        },
        complete: () => {
          this.discountDialogOpen = false;
        },
      });
    } else {
      this.lpDiscountDialogService.openDiscountDialogWithRequest(this.product, this.externalUnionId).subscribe({
        error: () => {
          this.discountDialogOpen = false;
        },
        complete: () => {
          this.discountDialogOpen = false;
        },
      });
    }
  }

  async onAction(action: LoyaltyProgramAction) {
    switch (action) {
      case 'delete':
        if (!(await this.loyaltyProgramApiService.deleteProduct(this.product?.id))) {
          return;
        }

        break;
      case 'publish':
        if (!(await this.loyaltyProgramApiService.publishProduct(this.product?.id))) {
          return;
        }

        break;
      case 'unpublish':
        if (!(await this.loyaltyProgramApiService.unpublishProduct(this.product?.id))) {
          return;
        }

        break;
      case 'edit':
        this.onUpdateProduct();

        return;
      case 'statistics':
        this.openStatisticsPage(this.product?.id);

        return;
      default:
        return;
    }

    const type = ACTION_MAP_RESULT[action];

    if (type) {
      this.resultDialogService.open({ type }).subscribe({
        complete: () => {
          if (type === ACTION_MAP_RESULT.delete) {
            // TODO: редирект на главную страницу
            this.router.navigate(['/loyalty']);
          } else {
            this.refresh(this.product?.id, this.unionId);
          }
        },
      });
    }
  }

  onUpdateProduct() {
    this.dialogService
      .open<void | string>(new PolymorpheusComponent(ProductDialogComponent, this.injector), {
        data: this.product,
        dismissible: false,
        closeable: false,
      })
      .subscribe(id => {
        if (id) {
          this.refresh(id, this.externalUnionId);
          this.resultDialogService.open({ type: 'updated' }).subscribe();
        }
      });
  }

  openStatisticsPage(id?: string) {
    if (!id) {
      return;
    }

    this.router.navigate(['/loyalty', 'statistics', id]);
  }

  saveRedirectLink(link: string) {
    if (!this.product?.id) return;

    this.loyaltyProgramApiService.saveRedirectLink(this.product?.id, link, this.externalUnionId);
  }

  private updateBreadcrumbs() {
    const { id: parentId, name: parentName = '' } = this.product?.categories[0]?.parent ?? {};

    this.breadcrumbs = [
      {
        caption: parentName,
        routerLink: '/loyalty',
        queryParams: parentId ? { categoryIds: parentId } : undefined,
      },
    ];

    this.cdr.markForCheck();
  }

  private async refresh(id?: string, unionId?: string, collectionId?: string, ref?: string) {
    this.productId = id;

    if (id) {
      this.loyaltyProgramApiService
        .getProductById(id, unionId, collectionId, ref)
        .pipe(takeUntil(this.destroyed$$))
        .subscribe();
    }
  }
}
